import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { first } from 'lodash';

import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';

import { type JobItemDetails, useJobItemDetails } from './use-job-item-details';
import { useJobItemVersions } from './use-job-item-versions';

// Loads a job item's versions and its details at `version`, or at the latest version when none is
// given. Callers that look at the same item through this hook share both queries.
export const useLatestJobItemDetails = ({
    architectureId,
    enabled = true,
    itemId,
    itemType,
    version,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version?: number;
}): {
    readonly details: UseQueryResult<JobItemDetails, Error>;
    readonly versions: UseQueryResult<JobProfileVersions, Error>;
} => {
    const versions = useJobItemVersions({ architectureId, enabled, itemId, itemType });
    const activeVersion = is.number(version) ? version.toString() : first(versions.data)?.version.toString();

    const details = useJobItemDetails({
        architectureId,
        enabled: enabled && !versions.isLoading,
        itemId,
        itemType,
        version: activeVersion,
    });

    return { details, versions };
};
